import * as InvoiceHelper from "helpers/InvoiceHelper";
import * as TerminalHelper from "helpers/TermialHelper";
import * as NumberFormatHelper from "helpers/NumberFormatHelper";
import DateTimeHelpers from "helpers/DateTimeHelpers";
import { TAX_PREFERENCES, TAX_TYPES, PAYMENT_MODES, CHARGE_LABELS } from "const";

function calculateSavings(items, terminalSettings, sale) {
  const mrpSavings = (items || []).reduce((acc, it) => {
    const mrp = Number(it.item?.mrp || it.mrp || 0);
    const basePrice = Number(it.item?.price ?? InvoiceHelper.getDisplayRate(it, terminalSettings.invoiceDisplayRate));
    const qty = Number(it.qty || 0);
    return mrp > basePrice ? acc + (mrp - basePrice) * qty : acc;
  }, 0);

  const transactionDiscount =
    Number(sale?.discount_applied_on_items || 0) + Number(sale?.extraDiscount || 0) + Number(sale?.discount || 0);

  return mrpSavings + transactionDiscount;
}

function calculateDisplayCharges(sale, showItemCharges, items) {
  let charges = sale?.totalChargeAmount || 0;
  let discount = Number(sale?.discount_applied_on_items || 0) + Number(sale?.discount || 0);
  if (showItemCharges) {
    charges = sale?.totalSaleChargeAmount || 0;
    const itemDiscounts = (items || []).reduce((sum, item) => sum + Number(item.totalDiscounts || 0), 0);
    discount -= itemDiscounts;
  }

  const label = showItemCharges ? CHARGE_LABELS.SALE_CHARGES : CHARGE_LABELS.TOTAL_CHARGES;
  return { displayTotalCharges: charges, displayTotalDiscount: discount, chargesLabel: label };
}

function getTerminalInvoiceFlags(terminalSettings = {}, sale = {}) {
  const showMRP = !!terminalSettings.printMrpOnInvoice;
  const paymentModeOnInvoice = !!terminalSettings.paymentModeOnInvoice;
  const printTotalSave = terminalSettings.printTotalSave;
  const showHSN = !!terminalSettings?.printHsnOnInvoice;
  const showToken = !!terminalSettings?.showTokenNumber;
  const showItemCharges = !!terminalSettings?.showItemChargesOnInvoice;
  const showPaymentQr =
    !!terminalSettings?.upi_id &&
    (!!terminalSettings?.printPaymentQrOnInvoice ||
      (sale?.payment_modes || []).some((pm) => pm.name?.toLowerCase() === PAYMENT_MODES.UPI));
  return { showMRP, paymentModeOnInvoice, printTotalSave, showHSN, showToken, showItemCharges, showPaymentQr };
}

function getShopGstInfo(shop = {}) {
  const isGstInvoice = shop?.tax_type === TAX_TYPES.GST && shop?.tax_preference !== TAX_PREFERENCES.NO_TAX;
  const headerRowSpan = isGstInvoice ? 2 : 1;
  return { isGstInvoice, headerRowSpan };
}

export function useInvoicePrintData(sale = {}, shop = {}) {
  const terminalSettings = TerminalHelper.getTerminalSettings() || {};
  const flags = getTerminalInvoiceFlags(terminalSettings, sale);
  const gstInfo = getShopGstInfo(shop);

  const date = DateTimeHelpers.parseLocalISO(sale?.time);
  const payable = Number(sale?.payable ?? 0);
  const tax = Number(sale?.tax ?? 0);
  const total = Number(sale?.total ?? 0);
  const invoiceFormat = InvoiceHelper.getInvoiceFormat() || {};
  const items = sale?.items || [];

  const totalSavings = calculateSavings(items, terminalSettings, sale);
  const chargeInfo = calculateDisplayCharges(sale, flags.showItemCharges, items);

  return {
    terminalSettings,
    date,
    payable,
    tax,
    total,
    invoiceFormat,
    items,
    totalSavings,
    ...flags,
    ...gstInfo,
    ...chargeInfo,
  };
}

export function useKOTInvoiceData(sale = {}) {
  const chairSuffix = sale?.table?.chair?.name ? ` > ${sale.table.chair.name}` : "";
  const tableName = sale?.table?.name ? `${sale.table.name}${chairSuffix}` : "-";
  const areaName = sale?.table?.area?.name || "-";
  const date = DateTimeHelpers.parseLocalISO(sale?.time);
  const formattedDate = DateTimeHelpers.getDate(date);
  const formattedTime = DateTimeHelpers.getLocalTime(date);
  const kotNo = sale?.local_id || "-";
  const staffName = sale?.salesperson?.name;
  const items = sale?.items || [];
  const totalQty = items.reduce((sum, it) => sum + Number(it.qty || 0), 0);

  return {
    tableName,
    areaName,
    date,
    formattedDate,
    formattedTime,
    kotNo,
    staffName,
    items,
    totalQty,
  };
}

function renderGstTaxRows(tax, styles) {
  const halfTax = tax / 2;
  return (
    <>
      <div className={styles.summaryGrid}>
        <div className={styles.taxLabel}>CGST</div>
        <div className={styles.summaryValue}>{NumberFormatHelper.roundOff(halfTax)}</div>
      </div>
      <div className={styles.summaryGrid}>
        <div className={styles.taxLabel}>SGST</div>
        <div className={styles.summaryValue}>{NumberFormatHelper.roundOff(halfTax)}</div>
      </div>
    </>
  );
}

export function renderTaxRows(tax, shop, styles) {
  if (tax === 0) return null;
  if (shop?.tax_type === TAX_TYPES.GST) return renderGstTaxRows(tax, styles);

  const taxLabel =
    shop?.tax_type === TAX_TYPES.IGST
      ? TAX_TYPES.IGST
      : shop?.tax_type === TAX_TYPES.VAT
        ? TAX_TYPES.VAT
        : TAX_TYPES.TAX;

  return (
    <div className={styles.summaryGrid}>
      <div className={styles.taxLabel}>{taxLabel}</div>
      <div className={styles.summaryValue}>{NumberFormatHelper.roundOff(tax)}</div>
    </div>
  );
}
